define((require) => {
    "use strict";

    var Preconditions   = require("shared/util/Preconditions");

    var ELEMENT_NODE = 1,
        TEXT_NODE = 3;

    var DomHandler = {

        clearSelection: function() {
            // Taken from:
            // http://stackoverflow.com/questions/3169786/clear-text-selection-with-javascript
            if (window.getSelection) {
                if (window.getSelection().empty) {  // Chrome
                    window.getSelection().empty();
                } else if (window.getSelection().removeAllRanges) {  // Firefox
                    window.getSelection().removeAllRanges();
                }
            } else if (document.selection) {  // IE?
                document.selection.empty();
            }
        },

        /**
         * Similar to `applyStyle`, only that it removes the style from the
         * selection (and all children).
         *
         * @param {jQuery} $el   - The jQuery parent.
         * @param {String} style - Css property that will be removed from the
         *                         elements.
         */
        removeStyle: function removeStyle($el, style) {
            Preconditions.is$element($el);
            Preconditions.isString(style);

            $el.find("div").css(style, "");
            DomHandler.removeAllSpanStyle($el[0].children, style);
        },

        /**
         * Removes all the spans with the style, assuming that each span can
         * only have one style.
         *
         * @param {Element[]} elements  jquery $els
         * @param {Object} style        the style to be matched
         */
        removeAllSpanStyle: function(elements, style) {
            var element,
                len = elements.length;

            while (len--) {
                element = elements[len];
                DomHandler.removeAllSpanStyle(element.children, style);

                if (element instanceof HTMLSpanElement && element.style[style]) {
                    element.outerHTML = element.innerHTML;
                }
            }
        },

        /**
         * getElementsBetweenNodes will return an unordered array of elements
         * between two arbitrarily positioned nodes in the dom. However, it all
         * siblings contained in a parent are included, then it will included
         * none of the siblings, but instead only their parent.
         *
         * The order of the two nodes passed in is important. The first node
         * must occur first in the DOM.
         *
         * @param {Node} startNode  - First node.
         * @param {Node} endNode    - Second node.
         * @return {Elements[]}     - Array of all elements between the two
         *                            nodes.
         *
         *
         * Example:
         *
         * Let's say we have a tree structure like this, with e, e1, e11, etc
         * being elements. The asterisks indicate elements we expect to be
         * included in the return value.
         *
         *  e
         *      e1
         *          e11
         *         *e12
         *              e121
         *                  e1211       <-- walk from here
         *                  e1212
         *              e122
         *         *e13
         *     *e2
         *      e3
         *         *e31
         *              e311
         *          e32
         *             *e321
         *             *e322            <-- to here
         *              e323
         *
         * DomHandler.getElementsBetweenNodes(e1211, e322, e)
         * => [e12, e13, e2, e322, e321, e31] // unordered
         *
         *
         */
        getElementsBetweenNodes: function(startNode, endNode, commonAncestor) {
            Preconditions.isElementOrTextNode(startNode);
            Preconditions.isElementOrTextNode(endNode);
            Preconditions.isElementOrTextNode(commonAncestor);

            if (startNode === endNode) {
                return [startNode];
            }

            // e1 in the above example
            var startChild = DomHandler._getChildContainingNode(startNode, commonAncestor),

                // e3 in the above example
                endChild = DomHandler._getChildContainingNode(endNode, commonAncestor),

                // [e2] in the above example
                inbetweenNodes = DomHandler._getNodesBetweenNodes(startChild, endChild),

                // [e12, e13] in above example
                startElements = DomHandler._getBranchElements(startNode, startChild, "after"),

                // [e322, e321, e31] in above example
                endElements = DomHandler._getBranchElements(endNode, endChild, "before");

            // Concatinate all arrays.
            return startElements.concat(inbetweenNodes).concat(endElements);
        },

        /**
         * Gets the immediate child of an ancestor node that is also parent of
         * the node passed in.
         *
         * Example:
         *
         * Dom structure:
         *  span.a
         *      span.b
         *      span.c
         *          span.d
         *              span.e
         *      span.f
         *
         * _getChildContainingNode(span.e, span.a) => span.c
         * _getChildContainingNode(span.d, span.a) => span.c
         * _getChildContainingNode(span.e, span.c) => span.d
         * _getChildContainingNode(span.b, span.a) => span.b
         * _getChildContainingNode(span.a, span.a) => span.a
         *
         * @private
         * @param {Node} node
         * @param {Node} ancestor
         * @return {Node}
         */
        _getChildContainingNode: function(node, ancestor) {
            Preconditions.isElementOrTextNode(node);
            Preconditions.isElementOrTextNode(ancestor);

            // There is no guarantee that the function was not called with
            // identical nodes.
            if (node === ancestor || node.parentNode === ancestor) {
                return node;
            }

            return DomHandler._getChildContainingNode(node.parentNode, ancestor);
        },

        /**
         * Returns an array of nodes between (not including) the two nodes
         * passed in.
         *
         * Assumes that the two nodes are siblings, and that nodeA is
         * positioned before nodeB.
         *
         * Example:
         *
         * Dom structure:
         *  span.a
         *      span.b
         *  span.c
         *  span.d
         *  span.e
         *  span.f
         *
         *  _getNodesBetweenNodes(span.a, span.c) => []
         *  _getNodesBetweenNodes(span.a, span.d) => [span.c]
         *  _getNodesBetweenNodes(span.c, span.f) => [span.d, span.e]
         *
         * @param {Node} nodeA
         * @param {Node} nodeB
         * @return {Node[]}
         */
        _getNodesBetweenNodes: function(nodeA, nodeB) {
            var elements = [];

            if (nodeA === nodeB) {
                return elements;
            }

            var nextSibling = nodeA.nextSibling;

            while (nextSibling && nextSibling !== nodeB) {
                elements.push(nextSibling);

                nextSibling = nextSibling.nextSibling;
            }

            return elements;
        },

        /**
         * See documentation and example for `getElementsBetweenNodes`. This
         * function walks the dom vertically and grabs siblings (either in
         * front or behind it).
         *
         * @private
         * @param {Node} startNode
         * @param {Node} stopNode
         * @param {String} siblingDirection
         * @param {Boolean} [childrenWasAdded]
         */
        _getBranchElements: function(startNode, stopNode, siblingDirection, childrenWasAdded) {
            Preconditions.isElementOrTextNode(startNode);
            Preconditions.isElementOrTextNode(stopNode);
            Preconditions.isString(siblingDirection);

            // Guard
            if (startNode === stopNode) {
                return childrenWasAdded ? [] : [startNode];
            }

            // We might pick the next or the previous siblings (depending
            // on which end of the selection we are working with).
            var includedSibling = siblingDirection === "after" ? "nextSibling" : "previousSibling",
                excludedSibling = siblingDirection === "after" ? "previousSibling" : "nextSibling",

                // Container for the returned elements.
                elements = [],

                // The first node to (possibly) be added to elements[]. If
                // children were added, then we should not collect the parent,
                // so we go straight for its (possible) sibling in the
                // direction of inclusion.
                node = childrenWasAdded ? startNode[includedSibling] : startNode,

                parentElements;

            // If we don't have any excluded siblings, then we skip this and
            // just take the entire parent.
            while (node && node[excludedSibling]) {
                elements.push(node);

                childrenWasAdded = true;

                node = node[includedSibling];
            }

            // Recursive call with parent (stops when it reaches `stopNode`).
            parentElements = DomHandler._getBranchElements(
                startNode.parentNode,
                stopNode,
                siblingDirection,
                childrenWasAdded
            );

            return elements.concat(parentElements);
        },

        /**
         * For each descendent text node in the passed nodes/elements, return
         * its closest parent element. This is used to computed the styles for
         * text nodes, because we can only get style values from element node.
         *
         * @param {Node[]} nodes
         * @returns {Array} An array of element nodes
         */
        getTextNodeParentList: function(nodes) {
            var elements = [],
                node,
                len = nodes.length,
                i = 0;

            for (; i < len; i++) {
                node = nodes[i];

                if (node.nodeType === TEXT_NODE) {
                    elements.push(DomHandler.getParentElement(node));
                } else {
                    elements = elements.concat(DomHandler.getTextNodeParentList(node.childNodes));
                }
            }

            return elements;
        },

        /**
         * IE does not support parentElement, it only supports parentNode, so we have to do this manually
         * This method assumes that we will always hit an element node before it goes wild.
         *
         * @param {Node} node  the dom node
         */
        getParentElement: function(node) {
            Preconditions.isElementOrTextNode(node);

            var parentNode = node.parentNode;

            if (parentNode.nodeType === ELEMENT_NODE) {
                return parentNode;
            }

            return DomHandler.getParentElement(parentNode);
        },

        /**
         * Given an element, get the closest parent element(or itself) for
         * which the style font-family is applied.
         *
         * @param {Element} element
         * @param {Element} container   - This function won't get beyond this.
         * @returns {Element}
         */
        getParentWithFontFamily: function(element, container) {
            Preconditions.isElement(element);

            if (element === container || (element.style && element.style["font-family"])) {
                return element;
            }

            return DomHandler.getParentWithFontFamily(DomHandler.getParentElement(element), container);
        }
    };

    return DomHandler;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/textWidget/textEditor/DomHandler.js